import sortBy from 'lodash/sortBy';
import { check as checkFF } from '@webapp-next/lib/feature-flag';
import getLibraryTemplateById from '@/modules/people/composables/get-library-template-by-id';
import { DateTime } from 'luxon';
// TODO: replace this with webappStore.user
import userSession from '@webapp-next/lib/user-session';
import {
  LEAVES,
  THIRD_PARTY_SYNC,
  THIRD_PARTY_SYNC_INDEXED,
  COUNTRY_CODE,
  LEVEL_ASSN_TYPE_MAPPING,
} from '../definitions';
import { getCurrencySymbol } from '../facades/business-configuration';
import { isEnterprise } from '@webapp-next/lib/company-subscription';
import useEmploymentStore from '@/modules/people/store/employment';
import usePeopleStore from '@/modules/people/store';
import useRootStore from '@/modules/people/store/root';
import useEmbedPermissions from '@webapp-next/lib/use-embed-permissions';
import { getPayCenterConfiguration } from '@webapp-next/modules/global-settings/composables/global-paycentres-config';

// TODO: remove this double declaration once all migrated to definitions/pay-rates
import {
  AWARDS_TYPE_ENUM,
  SALARY_DISTRIBUTION_ENUM,
  DEFAULT_AWARD_TYPE,
  ABP_IDENTIFIER,
  AWARDS_TYPES,
  PAY_RULE_ENUM,
  EMPLOYMENT_TYPE,
  employmentTypes,
  keysHourlyPayRate,
  keysHourlyPayRateForBulkAction,
} from '@/modules/people/definitions/pay-rates';
import useWorkDetailsStore from '@/modules/people/store/work-detail';
import { isCustomPayRate } from '@/modules/people/utils/is-custom-rates';
import { ContractStatuses } from '@/modules/people/modules/pay-rates/definitions';
import { checkSelectLevelVisible } from '@/modules/people/composables/people-edit-pay-detail-item';
import { getFieldNameFromRemunerationType } from '@/modules/people/utils/get-field-name-from-remuneration-type';
import isDeputyXeroEmbed from '@/modules/integrations/facades/isDeputyXeroEmbed';

export {
  AWARDS_TYPE_ENUM,
  SALARY_DISTRIBUTION_ENUM,
  DEFAULT_AWARD_TYPE,
  ABP_IDENTIFIER,
  AWARDS_TYPES,
  PAY_RULE_ENUM,
  EMPLOYMENT_TYPE,
  employmentTypes,
  keysHourlyPayRate,
  keysHourlyPayRateForBulkAction,
};

export function hasNameWithCustomEmploymentTermPrefix(name) {
  if (/^CT/.test(name)) {
    return true;
  }
  return false;
}

export function isLibraryAwardTemplate(contract) {
  return (
    contract?.IsTemplate &&
    !hasNameWithCustomEmploymentTermPrefix(contract?.Name)
  );
}

export function isSalaryContract(contract) {
  return contract.IsSalaryContract || contract.isSalaryContract;
}

export function isLibraryAwardImplementation(contract) {
  if (/^TP/.test(contract?.Name)) {
    return true;
  }

  // Area Based Pay
  if (
    checkFF('cjs-422-area-based-pay-v2') &&
    (contract?.Name?.includes(AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.LIBRARY) ||
      contract?.Name?.includes(AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.RATE_PER_DAY) ||
      contract?.Name?.includes(AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY_44))
  ) {
    return true;
  }

  return false;
}

export function isGlobalAward(contract) {
  if (/^global/.test(contract?.Id)) {
    return true;
  }
  return false;
}

export function getBasePayRule(contract) {
  if (contract?.BasePayRuleObject) {
    return contract.BasePayRuleObject;
  }

  const basePayRuleId = contract?.BasePayRule;
  const basePayRuleArray = contract?.PayRuleArray;

  if (!basePayRuleId || !Array.isArray(basePayRuleArray)) {
    return;
  }

  const basePayRule = basePayRuleArray.find(item => item.Id == basePayRuleId);

  return basePayRule || null;
}

export function getPayRules(contract) {
  // if ABP then filter
  if (contract.Code === AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.PREFIX) {
    const isAreaBasedPayV2Enabled = checkFF('cjs-422-area-based-pay-v2');
    if (!isAreaBasedPayV2Enabled || !contract.PayRuleOperationalUnitArray) {
      return contract?.PayRuleArray || [];
    }

    const basePayRules = contract.PayRuleOperationalUnitArray?.find(item => {
      return item.workAreas.length && item.workAreas[0] == '0';
    });

    const payRules = basePayRules?.payRuleIds?.map(payRuleId => {
      return contract?.PayRuleArray?.find(x => x.Id == payRuleId);
    });

    return payRules;
  } else {
    return contract?.PayRules || contract?.PayRuleArray || [];
  }
}

export function getSortedPayRules(payRules, payRateType) {
  if (payRateType === AWARDS_TYPE_ENUM.HOURLY.Code) {
    return sortHourlyPayRules(payRules);
  }

  if (payRateType === AWARDS_TYPE_ENUM.RATES_PER_DAY.Code) {
    return sortRatesPerDayPayRules(payRules);
  }

  return sortBy(payRules, [
    function(p) {
      return p.Id;
    },
  ]);
}

/**
 * Checks if the pay detail is the default one. Also checks that the account
 *  is Enterprise since only Enterprise uses the concept of default pay details.
 * @param {Object} payDetail
 * @return {Boolean}
 */
export function isDefaultPayDetail(payDetail) {
  return isEnterprise() && payDetail.isDefault;
}

export function isCustomEmploymentTermTemplate(contract) {
  let blnIsCustomEmploymentTermTemplate = false;
  if (hasNameWithCustomEmploymentTermPrefix(contract?.Name)) {
    const basePayRule = getBasePayRule(contract);
    if (basePayRule) {
      switch (basePayRule.RemunerationType) {
        case PAY_RULE_ENUM.REMUNERATION_TYPE.SALARY.Id:
          if (basePayRule.AnnualSalary == 0) {
            blnIsCustomEmploymentTermTemplate = true;
          }
          break;

        case PAY_RULE_ENUM.REMUNERATION_TYPE.HOURLY.Id:
        default:
          if (basePayRule.HourlyRate == 0) {
            blnIsCustomEmploymentTermTemplate = true;
          }
          break;
      }
    }
  }
  return blnIsCustomEmploymentTermTemplate;
}

export function isCustomEmploymentTerm(contract) {
  return (
    (hasNameWithCustomEmploymentTermPrefix(contract?.Name) ||
      isAreaBasedCustomTerm(contract?.Name)) &&
    !isCustomEmploymentTermTemplate(contract)
  );
}

export function isAreaBasedCustomTerm(contractName) {
  let blnHasNameWithAreaCTPrefix = false;

  if (contractName) {
    const objPattern = /^(Area Rate CT)/;
    if (objPattern.test(contractName)) {
      blnHasNameWithAreaCTPrefix = true;
    }
  }
  return blnHasNameWithAreaCTPrefix;
}

export function isFlatHourly40AndTimehalfOvertime(contract) {
  if (/^DP_OVT1/.test(contract?.Name)) {
    return true;
  }
  return false;
}

export function isSalary(contract) {
  const basePayRule = getBasePayRule(contract);
  if (
    basePayRule &&
    basePayRule.RemunerationType == PAY_RULE_ENUM.REMUNERATION_TYPE.SALARY.Id
  ) {
    return true;
  }
  return false;
}

/**
 * Check if the selected award template is 'Rates per Day'
 *
 * @param {Object} contract
 * @return {Boolean}
 */
export function isRatesPerDay(contract) {
  if (
    JSON.stringify(contract).includes(
      AWARDS_TYPE_ENUM.RATES_PER_DAY.ServiceHook
    )
  ) {
    return true;
  } else if (
    contract?.Code === 'global/7Days.json' ||
    contract?.Code === 'global/casual-2021-02-12-ratesperday-1' ||
    contract?.Name?.includes(AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.RATE_PER_DAY)
    // contract?.Name === 'Area Rate Rates per Day'
  ) {
    return true;
  } else {
    return (
      contract === AWARDS_TYPE_ENUM.RATES_PER_DAY.Id ||
      contract === 'global/casual-2021-02-12-ratesperday-1'
    );
  }
}

export function isHourly44(contract) {
  return !!(
    contract?.Name?.includes(AWARDS_TYPE_ENUM.HOURLY_44.Label) ||
    contract?.Code?.match(AWARDS_TYPE_ENUM.HOURLY_44.Pattern) ||
    contract.toString().match(AWARDS_TYPE_ENUM.HOURLY_44.Pattern)
  );
}

export function isFixedRate(contract) {
  return !!(
    contract?.Name?.includes(AWARDS_TYPE_ENUM.FIXED_RATE.Label) ||
    contract?.Code?.match(AWARDS_TYPE_ENUM.FIXED_RATE.Pattern) ||
    contract.toString().match(AWARDS_TYPE_ENUM.FIXED_RATE.Pattern)
  );
}

export function isExternalRate(contract) {
  return contract?.ThirdPartySync ? true : false;
}

export function isAreaBasedPay(contract) {
  if (contract?.Code == AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.PREFIX) {
    return true;
  }

  return;
}

export function getContractTypeForAreaBasedPay(contract) {
  if (contract?.Code != AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.PREFIX) {
    return;
  }

  let contractType = null;

  switch (contract.Name) {
    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY:
      contractType = AWARDS_TYPE_ENUM.HOURLY.Code;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME:
      contractType = AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY_44:
      contractType = AWARDS_TYPE_ENUM.HOURLY_44.Code;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.FIXED_RATE:
      contractType = AWARDS_TYPE_ENUM.FIXED_RATE.Code;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.RATE_PER_DAY:
      contractType = AWARDS_TYPE_ENUM.HOURLY.Code;
      break;
  }

  if (!contractType) {
    if (contract.Name?.startsWith(AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.LIBRARY)) {
      contractType = AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
    } else {
      contractType = AWARDS_TYPE_ENUM.HOURLY.Code;
    }
  }

  return contractType;
}

export function getContractIdForAreaBasedPay(contract) {
  if (contract?.Code != AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.PREFIX) {
    return;
  }

  let contractId = null;

  switch (contract.Name) {
    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY:
      contractId = AWARDS_TYPE_ENUM.HOURLY.Id;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME:
      contractId = AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Id;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.HOURLY_44:
      contractId = AWARDS_TYPE_ENUM.HOURLY_44.Id;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.FIXED_RATE:
      contractId = AWARDS_TYPE_ENUM.FIXED_RATE.Id;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.RATE_PER_DAY:
      contractId = contract?.Id || AWARDS_TYPE_ENUM.HOURLY.Id;
      break;
  }

  if (!contractId) {
    if (contract.Name?.startsWith(AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.LIBRARY)) {
      contractId = contract?.Id || AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Id;
    } else {
      contractId = AWARDS_TYPE_ENUM.HOURLY.Id;
    }
  }

  return contractId;
}

export function isNotABP(fileName) {
  return !String(fileName)?.includes(ABP_IDENTIFIER);
}

export function getPayRateType(contract) {
  if (contract.IsSalaryContract || contract.isSalaryContract) {
    return AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code;
  }
  if (isRatesPerDay(contract)) {
    return AWARDS_TYPE_ENUM.RATES_PER_DAY.Code;
  } else if (isHourly44(contract)) {
    return AWARDS_TYPE_ENUM.HOURLY_44.Code;
  } else if (isFixedRate(contract)) {
    return AWARDS_TYPE_ENUM.FIXED_RATE.Code;
  } else if (
    isCustomEmploymentTerm(contract) ||
    isCustomEmploymentTermTemplate(contract)
  ) {
    return AWARDS_TYPE_ENUM.CUSTOM_TERM.Code;
  } else if (
    isLibraryAwardImplementation(contract) ||
    isLibraryAwardTemplate(contract)
  ) {
    return AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
  } else if (isFlatHourly40AndTimehalfOvertime(contract)) {
    return AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code;
  } else if (isSalary(contract)) {
    return AWARDS_TYPE_ENUM.SALARY.Code;
  } else if (isExternalRate(contract)) {
    return AWARDS_TYPE_ENUM.EXTERNAL.Code;
  } else if (isAreaBasedPay(contract)) {
    return getContractTypeForAreaBasedPay(contract);
  } else if (isCustomPayRate(contract.Id)) {
    return AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
  } else {
    return AWARDS_TYPE_ENUM.HOURLY.Code;
  }
}

/**
 * Figures out which payRateType an Enterprise agreement belongs to and returns it
 * @param {Object} agreement
 * @param {boolean} isSalaryContract
 * @return {string}
 */
// TODO: we should lock this function using specifics params like { award = {}, basePayRule = {}, salaryPayRule = {} }
export function getEnterprisePayRateType(
  agreement = {},
  isSalaryContract = false
) {
  // this is for the view mode only when the employee doesn't have pay details
  if (!agreement.ContractObject) {
    return AWARDS_TYPE_ENUM.DEFAULT.Code;
  }
  if (isSalaryContract || agreement.ContractObject?.IsSalaryContract) {
    return AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code;
  }
  if (
    isCustomEmploymentTerm(agreement.ContractObject) ||
    isCustomEmploymentTermTemplate(agreement.ContractObject)
  ) {
    return AWARDS_TYPE_ENUM.CUSTOM_TERM.Code;
  } else if (
    agreement.SalaryPayRuleObject ||
    agreement.BasePayRuleObject?.RemunerationType ===
      PAY_RULE_ENUM.REMUNERATION_TYPE.SALARY.Id
  ) {
    return AWARDS_TYPE_ENUM.SALARY.Code;
  } else if (
    isLibraryAwardImplementation(agreement.ContractObject) ||
    isLibraryAwardTemplate(agreement.ContractObject) ||
    agreement.ContractObject.PpId
  ) {
    return AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
  } else {
    return AWARDS_TYPE_ENUM.OTHER.Code;
  }
}

export function getPayRateTypeList(payRateType) {
  return {
    hourly: payRateType === AWARDS_TYPE_ENUM.HOURLY.Code,
    hourly40Overtime:
      payRateType ===
      AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code,
    hourly44Overtime: payRateType === AWARDS_TYPE_ENUM.HOURLY_44.Code,
    fixedRate: payRateType === AWARDS_TYPE_ENUM.FIXED_RATE.Code,
    ratesPerDay: payRateType === AWARDS_TYPE_ENUM.RATES_PER_DAY.Code,
    salary: payRateType === AWARDS_TYPE_ENUM.SALARY.Code,
    salaryContract: payRateType === AWARDS_TYPE_ENUM.SALARY_CONTRACT.Code,
    library: payRateType === AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code,
    custom: payRateType === AWARDS_TYPE_ENUM.CUSTOM_TERM.Code,
    external: payRateType === AWARDS_TYPE_ENUM.EXTERNAL.Code,
    other: payRateType === AWARDS_TYPE_ENUM.OTHER.Code,

    // this is for the view mode only when the employee doesn't have pay details
    default: payRateType === AWARDS_TYPE_ENUM.DEFAULT.Code,
  };
}

export function initializeEmployeeContract(employeeContract) {
  if (!employeeContract) {
    return DEFAULT_AWARD_TYPE;
  }

  if (
    isLibraryAwardImplementation(employeeContract) ||
    isCustomEmploymentTerm(employeeContract) ||
    isRatesPerDay(employeeContract) ||
    isHourly44(employeeContract) ||
    isFixedRate(employeeContract) ||
    isSalaryContract(employeeContract)
  ) {
    return employeeContract.Id;
  } else if (isFlatHourly40AndTimehalfOvertime(employeeContract)) {
    return AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Id;
  } else if (isSalary(employeeContract)) {
    return AWARDS_TYPE_ENUM.SALARY.Id;
  } else if (isExternalRate(employeeContract)) {
    return AWARDS_TYPE_ENUM.EXTERNAL.Id;
  } else if (isAreaBasedPay(employeeContract)) {
    return getContractIdForAreaBasedPay(employeeContract);
  } else if (isCustomPayRate(employeeContract.Id)) {
    return employeeContract.Id;
  } else {
    return DEFAULT_AWARD_TYPE;
  }
}

export function getAwardLabel(AWARD_TYPE) {
  const awardConfig = AWARDS_TYPE_ENUM[AWARD_TYPE];
  return awardConfig?.Label || '';
}

export function getFilteredAwardList(awards, employmentType) {
  awards = awards || [];
  return awards.filter(award => {
    return award.EmploymentStatus === employmentType || !employmentType;
  });
}

export function getAwardListByEmploymentType(awards) {
  const reducedBase = {};
  reducedBase[EMPLOYMENT_TYPE.FULL_TIME] = [];
  reducedBase[EMPLOYMENT_TYPE.PART_TIME] = [];
  reducedBase[EMPLOYMENT_TYPE.CASUAL] = [];
  reducedBase[EMPLOYMENT_TYPE.CONTRACTOR] = [];

  return awards?.reduce((reduced, item) => {
    if (!isGlobalAward(item)) {
      if (item.EmploymentStatus === EMPLOYMENT_TYPE.FULL_TIME) {
        reduced[EMPLOYMENT_TYPE.FULL_TIME].push(item);
      } else if (item.EmploymentStatus === EMPLOYMENT_TYPE.PART_TIME) {
        reduced[EMPLOYMENT_TYPE.PART_TIME].push(item);
      } else if (item.EmploymentStatus === EMPLOYMENT_TYPE.CASUAL) {
        reduced[EMPLOYMENT_TYPE.CASUAL].push(item);
      } else if (
        item.EmploymentStatus === EMPLOYMENT_TYPE.CONTRACTOR &&
        isDeputyXeroEmbed()
      ) {
        reduced[EMPLOYMENT_TYPE.CONTRACTOR].push(item);
      }
    }

    return reduced;
  }, reducedBase);
}

/**
 * @param {String[]} skipBasicAwards
 *
 * @return {Object[]}
 */
export function getBasicAwardList(skipBasicAwards = []) {
  const AWARDS_BASIC = [
    AWARDS_TYPE_ENUM.HOURLY,
    AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME,
    AWARDS_TYPE_ENUM.SALARY,
  ].filter(x => !skipBasicAwards.includes(x.Code));

  return AWARDS_BASIC.map(award => {
    return {
      label: award.Label,
      value: award.Id ? award.Id : award.Code,
    };
  });
}

export function validatePayRate(data) {
  return /^\d+(\.\d{1,4})?$/.test(data);
}

export function parsePayRuleConfig(config) {
  try {
    return typeof config === 'string' ? JSON.parse(config) : config;
  } catch (e) {
    return {};
  }
}

export function isPayRuleApplicableToLevel(payRule, levelId) {
  const config = parsePayRuleConfig(payRule.Config);
  const levelAssnType = payRule.LevelAssnType ?? config?.levelAssnType;
  const levelIds = (payRule.Levels ?? payRule.LevelIds) || [];

  if (
    levelAssnType === LEVEL_ASSN_TYPE_MAPPING.CERTAIN_GROUP &&
    levelIds.length > 0
  ) {
    return levelIds.includes(levelId);
  } else if (levelAssnType === LEVEL_ASSN_TYPE_MAPPING.EVERYONE_EXCEPT) {
    return !levelIds.includes(levelId);
  }

  return true;
}

export function getSelectedDays(salaryDistributionConfigDays = []) {
  if (salaryDistributionConfigDays.length === 0) {
    return Object.keys(SALARY_DISTRIBUTION_ENUM.DAY_OF_WEEK.DAY_TO_INT);
  }

  return salaryDistributionConfigDays.map(
    item => SALARY_DISTRIBUTION_ENUM.DAY_OF_WEEK.INT_TO_DAY[parseInt(item)]
  );
}

export function sortRatesPerDayPayRules(payRules) {
  const orderedPayRuleDaySubstring = [
    'Mondays',
    'Tuesdays',
    'Wednesdays',
    'Thursdays',
    'Fridays',
    'Saturdays',
    'Sundays',
    'Public Holidays',
  ];

  return orderedPayRuleDaySubstring
    .map(dayName =>
      payRules.find(payRule => payRule.PayTitle?.includes?.(dayName))
    )
    .filter(item => !!item);
}

export function sortHourlyPayRules(payRules) {
  const keys = ['public holiday', 'sunday', 'saturday', 'weekday'];
  const sortedPayRules = [];

  keys.forEach(key => {
    const payRule = payRules.find(p =>
      p?.PayTitle?.toLowerCase().includes(key)
    );
    if (payRule) {
      sortedPayRules.push(payRule);
    }
  });
  return sortedPayRules;
}

export function isVisibleStandardHours(selectedLeaveRuleIds = []) {
  const rootState = useRootStore();
  let isLeaveInDay = false;
  const isFeatureEnabled = checkFF('cjs-354-custom-standard-hour-enabled');
  const allLeaveRules = rootState.getLeaveRules();

  selectedLeaveRuleIds?.forEach(x => {
    if (isLeaveInDay) {
      return;
    }
    const leaveRuleObject = allLeaveRules?.find(y => y.Id == x);

    if (leaveRuleObject && leaveRuleObject?.UnitType == 1) {
      isLeaveInDay = true;
    }
  });

  return isFeatureEnabled && isLeaveInDay;
}

export function getEmploymentCondition(
  listStandardHours = [],
  employmentConditionId
) {
  return listStandardHours?.find(x => x.Id == employmentConditionId);
}

export function getHourlyBlankRates() {
  const labels = [
    'Public holiday rate',
    'Sunday rate',
    'Saturday rate',
    'Weekday rate',
  ];

  const blankPayRates = [];
  labels.forEach((key, index) => {
    const rate = {
      Id: index + 1,
      PayTitle: key,
      HourlyRate: 0,
    };

    blankPayRates.push(rate);
  });

  return blankPayRates;
}

export function getHourly40OvertimeBlankRates() {
  return [
    {
      Id: 1,
      PayTitle: 'Weekly overtime 1.5',
      HourlyRate: null,
      IsMultiplier: true,
      MultiplierValue: 1.5,
    },
    {
      Id: 2,
      PayTitle: 'Daily $2.00',
      HourlyRate: 0,
      IsMultiplier: false,
    },
  ];
}

export function getBlankRates(awardType) {
  let payRules = [];
  switch (awardType) {
    case AWARDS_TYPE_ENUM.HOURLY.Code:
      payRules = getHourlyBlankRates();
      break;

    case AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code:
      payRules = getHourly40OvertimeBlankRates();
      break;
    case AWARDS_TYPE_ENUM.SALARY.Code:
    case AWARDS_TYPE_ENUM.RATES_PER_DAY.Code:
    case AWARDS_TYPE_ENUM.HOURLY_44.Code:
    case AWARDS_TYPE_ENUM.FIXED_RATE.Code:
    case AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code:
    case AWARDS_TYPE_ENUM.CUSTOM_TERM.Code:
    case AWARDS_TYPE_ENUM.EXTERNAL.Code:
      break;
  }

  return payRules;
}

export function getKeyHourlyPayRate(payTitle, isBulkAction) {
  const keys = isBulkAction
    ? keysHourlyPayRateForBulkAction
    : keysHourlyPayRate;

  if (payTitle?.toLowerCase()?.includes('weekday')) {
    return keys.weekday;
  } else if (payTitle?.toLowerCase()?.includes('saturday')) {
    return keys.saturday;
  } else if (payTitle?.toLowerCase()?.includes('sunday')) {
    return keys.sunday;
  } else if (payTitle?.toLowerCase()?.includes('public holiday')) {
    return keys.publicHoliday;
  }
}

export function sortHourlyPayRulesForPost(payRulesByKey) {
  const returnObject = {};
  ['PHolRate', 'SunRate', 'SatRate', 'WkdRate'].forEach(key => {
    returnObject[key] = payRulesByKey[key];
  });

  return returnObject;
}

export function getAreaBasedPayRequestObject(
  awardType,
  areaGroups,
  awardId,
  index = 0
) {
  const employmentState = useEmploymentStore();
  let request = null;

  switch (awardType) {
    case AWARDS_TYPE_ENUM.HOURLY.Code:
      request = getRequestObjectHourlyABP(areaGroups);
      break;
    case AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code:
      request = getRequestObjectHourly40AndOvertimeABP(areaGroups);
      break;
    case AWARDS_TYPE_ENUM.HOURLY_44.Code:
      request = getRequestObjectHourly44AndOvertimeABP(areaGroups, awardId);
      break;
    case AWARDS_TYPE_ENUM.FIXED_RATE.Code:
      request = getRequestObjectFixedRateABP(areaGroups, awardId);
      break;

    case AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code:
      request = getRequestObjectLibraryAwardABP(areaGroups, awardId);
      break;

    case AWARDS_TYPE_ENUM.CUSTOM_TERM.Code:
      request = getRequestObjectCustomAwardABP(areaGroups);
      break;

    case AWARDS_TYPE_ENUM.RATES_PER_DAY.Code:
      request = getRequestObjectRatesPerDayABP(
        areaGroups,
        employmentState.localPayDetail[index]?.contract
      );

      break;
  }

  return request;
}

export function getKeyRatesPerDay(payTitle) {
  const keys = {
    monday: 'Mondays',
    tuesday: 'Tuesdays',
    wednesday: 'Wednesdays',
    thursday: 'Thursdays',
    friday: 'Fridays',
    saturday: 'Saturdays',
    sunday: 'Sundays',
    publicHoliday: 'Public Holidays',
  };

  const DAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
    'public holiday',
  ];

  const found = DAYS.find(day => {
    return payTitle.toLowerCase().includes(day);
  });

  let key = '';
  if (found) {
    key = found == 'public holiday' ? 'publicHoliday' : found;

    return keys[key];
  }
}

export function getRequestObjectHourly(payRules, index = 0) {
  const employmentState = useEmploymentStore();
  const localPayDetail = employmentState.localPayDetail[index];
  const stateAreaBasedPay = localPayDetail.areaBasedPayV2;

  if (
    stateAreaBasedPay.areaSelected.length &&
    stateAreaBasedPay.areaGroups.length
  ) {
    // Area based pay is being used
    const payload = {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
    };
    const payRateObject = {};
    payRules?.forEach(payRule => {
      const key = getKeyHourlyPayRate(payRule.PayTitle);
      if (key) {
        payRateObject[key] = payRule.HourlyRate;
      }
    });

    payload.AreaOfWorkDefaultRates = sortHourlyPayRulesForPost(payRateObject);
    return { ...payload, ...stateAreaBasedPay.dataMap };
  } else {
    const payRateObject = {
      PayRatesType: AWARDS_TYPE_ENUM.HOURLY.Id,
    };
    payRules?.forEach(payRule => {
      const key = getKeyHourlyPayRate(payRule.PayTitle);
      if (key) {
        payRateObject[key] = payRule.HourlyRate;
      }
    });
    return payRateObject;
  }
}

export function getRequestObjectHourlyABP(areaGroups) {
  const arrRequest = [];
  areaGroups?.forEach(areaGroup => {
    const payload = {};
    const areaIds = areaGroup.areas?.map(area => area.link);
    payload.workAreas = areaIds;
    areaGroup.payRules?.forEach(payRule => {
      const key = getKeyHourlyPayRate(payRule.PayTitle);
      if (key) {
        payload[key] = payRule.HourlyRate;
      }
    });

    const sortedPayRulesObject = sortHourlyPayRulesForPost(payload);

    arrRequest.push({ ...payload, ...sortedPayRulesObject });
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

export function getRequestObjectHourly40AndOvertime(payRule, index = 0) {
  const employmentState = useEmploymentStore();
  const localPayDetail = employmentState.localPayDetail[index];
  const stateAreaBasedPay = localPayDetail.areaBasedPayV2;

  if (
    stateAreaBasedPay.areaSelected.length &&
    stateAreaBasedPay.areaGroups.length
  ) {
    // Area based pay is being used

    return {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
      AreaOfWorkDefaultRates: {
        Flat40HourlyBaseRateAndTimehalfOvertime: payRule.HourlyRate,
      },
      ...stateAreaBasedPay.dataMap,
    };
  } else {
    return {
      PayRatesType: AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Id,
      Flat40HourlyRateAndTimehalfOvertime: payRule.HourlyRate,
    };
  }
}

export function getRequestObjectHourly44AndOvertime(
  awardId,
  payRules,
  index = 0
) {
  const employmentState = useEmploymentStore();
  const localPayDetail = employmentState.getLocalPayDetailByIndex(index);
  const stateAreaBasedPay = localPayDetail.areaBasedPayV2;

  if (
    stateAreaBasedPay.areaSelected.length &&
    stateAreaBasedPay.areaGroups.length
  ) {
    // Area based pay is being used
    const templateTermParams = getTemplateTermParams(awardId);

    return {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
      AreaOfWorkDefaultRates: {
        ...templateTermParams,
        TemplateEmploymentTermOverrideMap: getContractOverrideMapHourly44(
          payRules
        ),
      },
      ...stateAreaBasedPay.dataMap,
    };
  } else {
    const templateTermParams = getTemplateTermParams(awardId);

    return {
      PayRatesType: AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Id,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap: getContractOverrideMapHourly44(
        payRules
      ),
    };
  }
}

export function getRequestObjectFixedRate(awardId, payRules, index = 0) {
  const employmentState = useEmploymentStore();
  const localPayDetail = employmentState.localPayDetail[index];
  const stateAreaBasedPay = localPayDetail.areaBasedPayV2;

  if (
    stateAreaBasedPay.areaSelected.length &&
    stateAreaBasedPay.areaGroups.length
  ) {
    // Area based pay is being used
    const templateTermParams = getTemplateTermParams(awardId);

    return {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
      AreaOfWorkDefaultRates: {
        ...templateTermParams,
        TemplateEmploymentTermOverrideMap: getContractOverrideMapFixedRate(
          payRules
        ),
      },
      ...stateAreaBasedPay.dataMap,
    };
  } else {
    const templateTermParams = getTemplateTermParams(awardId);

    return {
      PayRatesType: AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Id,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap: getContractOverrideMapFixedRate(
        payRules
      ),
    };
  }
}

export function getRequestObjectHourly40AndOvertimeABP(areaGroups) {
  const arrRequest = [];
  areaGroups?.forEach(areaGroup => {
    const payload = {};
    const areaIds = areaGroup.areas?.map(area => area.link);
    payload.workAreas = areaIds;

    const basePayRule = areaGroup.payRules?.find(payRule => {
      return !payRule.IsMultiplier;
    });

    payload['Flat40HourlyBaseRateAndTimehalfOvertime'] =
      basePayRule?.HourlyRate || 0;

    arrRequest.push(payload);
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

export function getRequestObjectHourly44AndOvertimeABP(areaGroups, awardId) {
  const arrRequest = [];
  areaGroups?.forEach(areaGroup => {
    const areaIds = areaGroup.areas?.map(area => area.link);
    const workAreas = areaIds;

    const templateTermParams = getTemplateTermParams(awardId);
    const TemplateEmploymentTermOverrideMap = getContractOverrideMapHourly44(
      areaGroup.payRules
    );

    arrRequest.push({
      workAreas,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap,
    });
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

export function getRequestObjectFixedRateABP(areaGroups, awardId) {
  const arrRequest = [];
  areaGroups?.forEach(areaGroup => {
    const areaIds = areaGroup.areas?.map(area => area.link);
    const workAreas = areaIds;

    const templateTermParams = getTemplateTermParams(awardId);
    const TemplateEmploymentTermOverrideMap = getContractOverrideMapFixedRate(
      areaGroup.payRules
    );

    arrRequest.push({
      workAreas,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap,
    });
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

/**
 * @param {Array} payRules
 * @param {Object} areaRate
 * @param {Object} basePayRule
 *
 * @return {Array} formatted pay rules
 */
export function formatPayRulesV2({
  payRules = [],
  areaRate = {},
  basePayRule = {},
}) {
  const newRules = [];
  payRules?.forEach(rule => {
    if (rule.hourlyRate) {
      rule.hourlyRate = Number(areaRate.baseRate);
    }
    const newRule = {
      ...rule,
    };
    if (rule.Config) {
      newRule.Config = JSON.parse(rule.Config);
    }
    // Sync base pay rule to the list of pay rules
    if (basePayRule?.Id === rule.Id) {
      newRule.HourlyRate = basePayRule.HourlyRate;
    }
    newRules.push({
      ...newRule,
    });
  });
  return newRules;
}

/**
 *
 * @param {Object} customPayData
 * @param {Object} contract
 *
 * @return{Object[]}
 */
export function processCustomPayData({ customPayData }) {
  const peopleState = usePeopleStore();
  const rootState = useRootStore();
  const areaGroups = [];

  // populate with basePayRate
  customPayData.areaRates?.forEach(item => {
    const selectedTerm = rootState.setup.EmploymentTermArray.find(
      term => term.Id === item?.contractId
    );
    if (!selectedTerm) {
      return;
    }
    const selectedContract = peopleState.customPayRates.contracts.find(
      contract => contract.id === item?.contractId
    );

    let basePayRule =
      selectedContract.payRules.find(
        payRule => payRule.Id === selectedTerm?.BasePayRule
      ) || {};

    let defaultBaseRate = null;
    if (selectedContract.levels.length > 0 && item.levelId !== null) {
      const selectedLevel = selectedContract.levels.find(
        level => level.id === item.levelId
      );
      // TODO: Should compare item.baseRate === NULL
      if (selectedLevel?.baseRate === item.baseRate) {
        defaultBaseRate = basePayRule.HourlyRate;
      }
    }

    basePayRule = { ...basePayRule, HourlyRate: Number(item.baseRate) };
    const payRules = formatPayRulesV2({
      payRules: selectedContract.payRules,
      areaRate: item,
      basePayRule,
    });
    const areas = [];

    item.areaIds.forEach(areaId => {
      areas.push({
        link: areaId,
        value: rootState.areasIndexed[areaId]?.OperationalUnitName || '',
      });
    });
    const areaGroup = {
      areas,
      basePayRule,
      payRules,
      contract: selectedContract.id,
      baseRate: item.baseRate,
    };
    areaGroup.level = item.levelId;
    areaGroup.contractLevels = selectedContract.levels.map(level => ({
      Id: level.id,
      Title: level.title,
      BaseRate: level.baseRate,
      LevelKsuid: level.levelKsuid,
    }));

    areaGroup.defaultBaseRate = defaultBaseRate;

    areaGroups.push(areaGroup);
  });
  return areaGroups;
}

export function getAreaBasedPayData({
  contract = {},
  payDetail = {},
  customPayData = {},
}) {
  const peopleState = usePeopleStore();

  if (peopleState.isPayPolicy(contract.Id)) {
    return processCustomPayData({ customPayData, contract });
  }

  if (contract.Code != AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.PREFIX) {
    return [];
  }

  if (!contract.PayRuleOperationalUnitArray) {
    return [];
  }

  const areaBasedPayRules = contract.PayRuleOperationalUnitArray?.filter(
    item => {
      return item.workAreas.length && item.workAreas[0] != '0';
    }
  );

  const areaGroups = [];

  let count = 1;

  areaBasedPayRules?.forEach(abpItem => {
    const areaGroup = {};
    const areaIds = abpItem?.workAreas?.map(x => parseInt(x));
    const { payRateType, areaBasedPayV2 } = payDetail || {};

    areaGroup.areas = areaIds.map(x =>
      areaBasedPayV2?.areaList?.find(y => y.link == x)
    );

    if (areaBasedPayV2?.areaSelected) {
      areaBasedPayV2.areaSelected = [
        ...areaBasedPayV2.areaSelected,
        ...areaGroup.areas,
      ];
    }

    areaGroup.id = count++;

    if (payRateType == AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code) {
      const { awardId, basePayRule, payRules } = getLibraryRatesAreaGroup(
        contract,
        abpItem
      );
      areaGroup.contract = awardId;
      areaGroup.basePayRule = basePayRule;
      areaGroup.payRules = payRules;
    } else if (payRateType == AWARDS_TYPE_ENUM.CUSTOM_TERM.Code) {
      const { awardId, basePayRule, payRules } = getCustomRatesAreaGroup(
        contract,
        abpItem
      );
      areaGroup.contract = awardId;
      areaGroup.basePayRule = basePayRule;
      areaGroup.payRules = payRules;
    } else {
      areaGroup.payRules = abpItem?.payRuleIds?.map(payRuleId => {
        return contract?.PayRuleArray?.find(x => x.Id == payRuleId);
      });
    }

    areaGroups.push(areaGroup);
  });
  return areaGroups;
}

function getLibraryRatesAreaGroup(contract, abpItem) {
  const allPayRules = abpItem?.payRuleIds?.map(payRuleId => {
    return contract?.PayRuleArray?.find(x => {
      return x.Id == payRuleId;
    });
  });

  const multiplierRule = allPayRules?.find(x => x.IsMultiplier);

  const basePayRule = allPayRules?.find(
    x => x.Id == multiplierRule?.MultiplierBaseRate
  );

  const payRules = allPayRules?.filter(x => {
    return x.Id != basePayRule?.Id;
  });

  // The award is available in the `Comment` field of the base pay rule :(
  const awardId = getAwardFromPayRuleComment(basePayRule?.Comment ?? '');

  return {
    awardId,
    basePayRule,
    payRules,
  };
}

function getCustomRatesAreaGroup(contract, abpItem) {
  const allPayRules = abpItem?.payRuleIds?.map(payRuleId => {
    return contract?.PayRuleArray?.find(x => {
      return x.Id == payRuleId;
    });
  });

  const basePayRule = allPayRules?.find(x => x.Comment);

  const payRules = allPayRules?.filter(x => {
    return x.Id != basePayRule?.Id;
  });

  // The award is available in the `Comment` field of the base pay rule :(
  const awardId = getAwardFromPayRuleComment(basePayRule?.Comment ?? '');

  return {
    awardId,
    basePayRule,
    payRules,
  };
}

function getAwardFromPayRuleComment(comment) {
  const commentComponents = comment.split('|');
  const award = commentComponents[commentComponents.length - 1];
  return parseInt(award) || award || '';
}

export function getAreaListForABP(payDetail, useWorkdetailLocations = false) {
  const rootState = useRootStore();
  const workDetailsState = useWorkDetailsStore();
  const peopleState = usePeopleStore();

  const areas = rootState.activeAreas;
  const companies = rootState.setup?.WorkplaceArray || [];
  const companyIds = useWorkdetailLocations
    ? workDetailsState.locations?.map(location => location.link)
    : peopleState.employee?.WorkplaceArray?.map(workplace => workplace.Company);

  const employeeLocations = companyIds?.map(companyId => {
    return companies?.find(x => x.Id == companyId);
  });

  const areaList = [];

  employeeLocations?.forEach(company => {
    const areasByCompany = areas?.filter(area => {
      return area.Company === company.Id;
    });

    const filteredAreas = areasByCompany?.map(x => {
      // TODO: this is a hidden side effect how can we remove if from here?
      // Keep this info handy for later
      payDetail.areaBasedPayV2.areaIndexed[x.Id] = {
        areaName: x.OperationalUnitName,
        areaColor: x.Colour ? x.Colour : `#000`,
        companyName: company.CompanyName,
      };

      return {
        link: x.Id,
        value: `${x.OperationalUnitName}`,
      };
    });

    areaList.push(...filteredAreas);
  });

  return areaList;
}

export function findPayRateByName(strType, payRates) {
  const payRate = payRates?.find(item =>
    item.PayTitle?.toLowerCase().includes(strType.toLowerCase())
  );
  return payRate ? payRate.HourlyRate : 0;
}

export function humanizeHourlyPayRates(payRates) {
  const CURRENCY_SYMBOL = getCurrencySymbol();
  const weekDayPayRate = findPayRateByName('Weekday', payRates);
  const saturdayPayRate = findPayRateByName('Saturday', payRates);
  const sundayPayRate = findPayRateByName('Sunday', payRates);
  const publicHolidayPayRate = findPayRateByName('Public Holiday', payRates);

  // TODO: add translations here
  return [
    {
      label: 'Weekday rate',
      value: `${CURRENCY_SYMBOL}${weekDayPayRate} per hour`,
    },
    {
      label: 'Saturday rate',
      value: `${CURRENCY_SYMBOL}${saturdayPayRate} per hour`,
    },
    {
      label: 'Sunday rate',
      value: `${CURRENCY_SYMBOL}${sundayPayRate} per hour`,
    },
    {
      label: 'Public holiday rate',
      value: `${CURRENCY_SYMBOL}${publicHolidayPayRate} per hour`,
    },
  ];
}

export function humanizeHourly40OvertimePayRates(payRates) {
  const CURRENCY_SYMBOL = getCurrencySymbol();
  const dailyRate = payRates?.find(x => !x.IsMultiplier);
  const overtimeRate = payRates?.find(x => x.IsMultiplier);

  // TODO: add translations here
  return [
    {
      label: 'Daily rate',
      value: `${CURRENCY_SYMBOL}${dailyRate?.HourlyRate || 0} per hour`,
    },
    {
      label: 'Overtime rate',
      value: `${CURRENCY_SYMBOL}${formatMultiplierRate(
        dailyRate.HourlyRate * overtimeRate.MultiplierValue
      ) || 0} per hour`,
      multiplier: `(Base rate x ${overtimeRate.MultiplierValue})`,
    },
  ];
}

/**
 * @param{Object} payDetail
 *
 * @return{undefined}
 */
export function resetAreaBasedPay(payDetail = {}) {
  if (payDetail) {
    const stateAreaBasedPay = payDetail.areaBasedPayV2;
    stateAreaBasedPay.areaSelected = [];
    stateAreaBasedPay.validationError = {};
    stateAreaBasedPay.areaGroups = [];
  }
}

/**
 * @return{Object[]}
 */
export function getGlobalAwards() {
  const rootState = useRootStore();
  const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();

  let globalAwards = rootState.getEmploymentTerms()?.filter(item => {
    return isGlobalAward(item);
  });

  globalAwards = globalAwards?.map(item => {
    const newItem = isMultiPayDetailsAvailableOnPremium ? { ...item } : {};
    newItem.label = item?.Name;
    newItem.value = item?.Id;
    return newItem;
  });
  return globalAwards;
}

/**
 * this function will check if the employee current award is in the list if not will add it
 * TODO: create a cleaver index for these awards and avoid this function for loop
 *
 * @return{Object[]}
 */
export function getAllAwards() {
  const rootState = useRootStore();
  const peopleState = usePeopleStore();

  // TODO: make it accept contract id
  let contracts = [peopleState.employee?.Contract];
  let listContract = rootState.setup.EmploymentTermArray || [];
  if (getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium()) {
    contracts =
      peopleState.employee?.Agreements?.map(
        agreement => agreement.ContractObject
      ) ?? [];
  }

  contracts.forEach(contract => {
    if (!contract?.Id) {
      return;
    }

    const found = listContract?.find(x => x.Id == contract.Id); // comparing numbers with string this is why it has to 2 equals

    // Area based pay library award is not available in the
    // Global list `EmploymentTermArray`
    if (!found) {
      listContract = [...listContract, contract];
    }
  });

  return listContract;
}

/**
 * @param{String} strConnectedPayrollServicePrefix
 *
 * @return{String}
 */
export function getExternalRateLabel(strConnectedPayrollServicePrefix = null) {
  const rootState = useRootStore();
  const peopleState = usePeopleStore();

  let strLabel = '';

  const intWorkplaceIdArray =
    peopleState.employee?.WorkplaceArray?.map(x => x.Company) ||
    rootState.setup?.WorkplaceArray?.map(x => x.Id) ||
    [];

  if (!strConnectedPayrollServicePrefix) {
    strConnectedPayrollServicePrefix = getConnectedPayrollService(
      intWorkplaceIdArray
    );
  }

  if (
    strConnectedPayrollServicePrefix &&
    THIRD_PARTY_SYNC[strConnectedPayrollServicePrefix]
  ) {
    strLabel = THIRD_PARTY_SYNC[strConnectedPayrollServicePrefix].PayrollLabel;
  }

  return strLabel;
}

/**
 * @param{Object} opt
 *   @param{Object} opt.employmentType
 *   @param{Boolean} opt.libraryAwardsOnly
 *   @param{Boolean} opt.customAwardsOnly
 *   @param{String[]} opt.skipBasicAwards
 *   @param{Boolean} opt.displayExternalRates
 *   @param{Boolean} opt.strConnectedPayrollServicePrefix
 *   @param{(String|Number)} opt.awardId
 *   @param{Boolean} opt.xeroEnabled
 *   @param{Boolean} opt.employmentContract
 *   @param{Number} opt.payDetailsPositionIndex
 *
 * @return{Object[]}
 */
export function getPayRateOptions({
  employmentType = null,
  libraryAwardsOnly = false,
  customAwardsOnly = false,
  skipBasicAwards = [],
  displayExternalRates = true,
  strConnectedPayrollServicePrefix = null,
  awardId = undefined,
  xeroEnabled = false,
  employmentContract = null,
  payDetailsPositionIndex = 0,
  checkPayPolicy = false,
  isPayPolicy = false,
  skipABPIds = [],
  getContractOverrideMap = false,
  disableBasicPayOptions = false,
  areaContractId = undefined,
  ignoreNewSalaryContract = false,
} = {}) {
  const isMultiPayDetailsAvailableOnPremium = getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium();
  let globalAwards = [];
  const regionalAwards = [];
  const customAwards = [];
  const basicAwards =
    libraryAwardsOnly || customAwardsOnly
      ? []
      : getBasicAwardList(skipBasicAwards);

  const employmentState = useEmploymentStore();
  // TODO: replace this with webappStore.user
  const { Country } = userSession();
  const skipFilter = !employmentType || Country !== 'AU';
  const awardsByEmploymentType = skipFilter
    ? getAllAwards()
    : employmentState.payRatesIndexedByEmploymentType[employmentType];

  // It is required to add the global awards for 'AU' when is set employment type.
  if (Country === 'AU' && employmentType) {
    globalAwards = getGlobalAwards();
  }

  awardsByEmploymentType?.forEach(award => {
    const newSalaryPrbDisabled =
      !checkFF('pre-4268-new-salary-prb-enabled') || ignoreNewSalaryContract;
    if (award.IsSalaryContract && newSalaryPrbDisabled) {
      return;
    }
    /* Ignore this if the status not active and current user not be assigned with */
    if (
      award.Status !== undefined &&
      award.Status !== ContractStatuses.ACTIVE
    ) {
      // An exception for the contract which is assigned before
      if (award.Id !== awardId && award.Id !== areaContractId) {
        return;
      }
    }
    const option = {
      ...(isMultiPayDetailsAvailableOnPremium ? award : {}),
      label: award.Name,
      value: award.Id ? award.Id : award.Code,
    };

    if (!customAwardsOnly && isLibraryAwardTemplate(award)) {
      if (isGlobalAward(award)) {
        globalAwards.push(option);
      } else {
        if (!checkPayPolicy) {
          // If checkPayPolicy is false, always push the option to regionalAwards
          regionalAwards.push(option);
        } else {
          // checkPayPolicy is true, consider isPayPolicy and award.IsPayPolicy conditions
          const isPayPolicyMatch = isPayPolicy && award?.IsPayPolicy;
          const isNotPayPolicyMatch = !isPayPolicy && !award?.IsPayPolicy;

          if (isPayPolicyMatch || isNotPayPolicyMatch) {
            // If either isPayPolicy and award.IsPayPolicy match, or isPayPolicy and award.IsPayPolicy both don't match, push the option to regionalAwards
            regionalAwards.push(option);
          }
        }
      }
    } else if (!libraryAwardsOnly && isLibraryAwardImplementation(award)) {
      // add the library implementation only if the people to be edited have it
      if (award?.Id == awardId) {
        customAwards.push(option);
      }
    } else if (
      // If checkPayPolicy is false, always push the option to customAwards
      !checkPayPolicy &&
      (isCustomEmploymentTerm(award) || isCustomEmploymentTermTemplate(award))
    ) {
      customAwards.push(option);
    } else if (checkPayPolicy && isPayPolicy) {
      if (
        award?.IsPayPolicy &&
        (skipABPIds.includes(award.Id) ||
          (isCustomPayRate(award.Id) &&
            (!employmentType || employmentType === award.EmploymentStatus)))
      ) {
        customAwards.push(option);
      }
    } else if (
      isCustomPayRate(award.Id) &&
      (!employmentType || employmentType === award.EmploymentStatus)
    ) {
      customAwards.push(option);
    }
  });

  // Basic awards
  let basicAndGlobalAwardsGroup = undefined;
  let basicAndGlobalAwards = [...basicAwards, ...globalAwards];

  const embedPermissionsCheck = useEmbedPermissions('people');
  const hasPermission = embedPermissionsCheck('hourly-pay-rates', true);
  if (!hasPermission) {
    // If the user does not have permission to view default hourly pay rates, filter out the basic and global awards
    const hideAwardCodes = [
      AWARDS_TYPE_ENUM.HOURLY.Label.toUpperCase(),
      AWARDS_TYPE_ENUM.RATES_PER_DAY.Label.toUpperCase(),
    ];
    basicAndGlobalAwards = basicAndGlobalAwards.filter(
      award => !hideAwardCodes.includes(award.label?.toUpperCase()) // Filter out the awards with the specified codes
    );
  }

  if (!(libraryAwardsOnly || customAwardsOnly) && !disableBasicPayOptions) {
    basicAndGlobalAwardsGroup = {
      label: '',
      options: basicAndGlobalAwards,
    };
  }

  // Library awards
  let libraryAwardsGroup = undefined;
  if (!customAwardsOnly && regionalAwards.length) {
    libraryAwardsGroup = {
      label: AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Label,
      options: [...regionalAwards],
    };
  }

  // Custom awards
  let customAwardsGroup = undefined;
  if (
    (!libraryAwardsOnly && customAwards.length) ||
    (checkPayPolicy && isPayPolicy)
  ) {
    customAwardsGroup = {
      label: AWARDS_TYPE_ENUM.CUSTOM_TERM.Label,
      options: [...customAwards],
    };
  }

  // External awards
  let externalAwardsGroup = undefined;
  if (displayExternalRates) {
    const awardLabel = getExternalRateLabel(strConnectedPayrollServicePrefix);
    if (awardLabel) {
      externalAwardsGroup = {
        label: AWARDS_TYPE_ENUM.EXTERNAL.Label,
        options: [
          {
            label: awardLabel,
            value: AWARDS_TYPE_ENUM.EXTERNAL.Id,
          },
        ],
      };
    }
  }

  const xeroDisabled = checkFF(
    'eis-395-hide-xero-option-from-pay-rate-dropdown-on-employee-profile'
  );

  // If the xero disabled feature flag is enabled, or the employment contract is external, inject the Xero option
  if (!xeroDisabled || employmentContract === AWARDS_TYPE_ENUM.EXTERNAL.Id) {
    // Inject third party pay rate option, locked to Xero only for now
    if (
      xeroEnabled ||
      employmentContract === THIRD_PARTY_SYNC_INDEXED.Xero.ContractId
    ) {
      const hasXero =
        basicAndGlobalAwardsGroup?.options?.findIndex(
          ({ value }) => value === THIRD_PARTY_SYNC_INDEXED.Xero.ContractId
        ) > -1;

      if (!hasXero) {
        const xeroOption = {
          label: THIRD_PARTY_SYNC_INDEXED.Xero.PayrollLabel,
          value: THIRD_PARTY_SYNC_INDEXED.Xero.ContractId,
        };
        if (!basicAndGlobalAwardsGroup) {
          basicAndGlobalAwardsGroup = {
            label: '',
            options: [xeroOption],
          };
        } else {
          basicAndGlobalAwardsGroup.options.push(xeroOption);
        }
      }
    }
  }
  return [
    customAwardsGroup,
    libraryAwardsGroup,
    basicAndGlobalAwardsGroup,
    externalAwardsGroup,
  ].filter(Boolean);
}

export async function getLibraryRatesByTemplate(awardId, libraryTemplate) {
  const data = libraryTemplate ?? (await getLibraryTemplateById(awardId)).data;
  const award = data?.DeputecEmploymentContract;
  const payRateType = isRatesPerDay(awardId)
    ? AWARDS_TYPE_ENUM.RATES_PER_DAY.Code
    : AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
  const payRules = data?.DeputecPayRules
    ? getSortedPayRules(data.DeputecPayRules, payRateType)
    : [];
  const levels = data?.DeputecEmploymentContractLevels || [];
  if (levels.length > 0) {
    /* Update each payRules with Levels to match with the format of employee response */
    payRules.forEach(payRule => {
      if (!payRule.Levels) {
        payRule.Levels = [];
        levels.forEach(level => {
          if (level.PayRules.includes(payRule.Id)) {
            payRule.Levels.push(level.Id ?? level.LevelKsuid);
          }
        });
      }
    });
  }
  // in case Id is missing we use LevelKsuid
  levels.forEach(level => {
    if (!level.Id) {
      level.Id = level.LevelKsuid;
    }
  });
  const employmentState = useEmploymentStore();
  employmentState.payRateName = award?.Name || '';

  return {
    awardName: award?.Name || '',
    basePayRule: award?.BasePayRuleObject,
    payRules,
    leaveRules: data?.DeputecEmploymentContractLeaveRules || [],
    levels,
    isSalaryContract: data?.IsSalaryContract,
  };
}

export async function getCustomAwardData(awardId, libraryTemplate) {
  const employmentState = useEmploymentStore();
  const data = libraryTemplate ?? (await getLibraryTemplateById(awardId)).data;
  const award = data?.DeputecEmploymentContract;
  employmentState.payRateName = award?.Name || '';

  return {
    awardName: award?.Name || '',
    basePayRule: award?.BasePayRuleObject,
    payRules: data?.DeputecPayRules || [],
    leaveRules: data?.DeputecEmploymentContractLeaveRules || [],
  };
}

export function getContractOverrideMap(basePayRule, payRules) {
  if (typeof basePayRule?.HourlyRate === 'string') {
    basePayRule.HourlyRate = parseFloat(basePayRule.HourlyRate);
  }
  const mapPayRules = {};
  if (basePayRule) {
    mapPayRules[basePayRule.Id] = {
      field: 'HourlyRate',
      value: basePayRule.HourlyRate ? `${basePayRule.HourlyRate}` : 0,
      payTitle: basePayRule.PayTitle,
      crId: basePayRule.CrId ?? null,
    };
  }
  payRules?.forEach(item => {
    if (!item.IsMultiplier) {
      const payRateValueFieldName = getFieldNameFromRemunerationType(item);
      const rate = item[payRateValueFieldName];
      mapPayRules[item.Id] = {
        field: payRateValueFieldName,
        value: rate ? `${rate}` : 0,
        payTitle: item.PayTitle,
      };
    }
  });
  return mapPayRules;
}

export function getContractOverrideMapRatesPerDay(payRules) {
  const mapPayRules = {};
  payRules?.forEach(item => {
    if (!item.IsMultiplier) {
      mapPayRules[item.Id] = {
        field: 'HourlyRate',
        value: `${item.HourlyRate || ''}`,
        payTitle: getKeyRatesPerDay(item.PayTitle),
      };
    }
  });
  return mapPayRules;
}

export function getContractOverrideMapHourly44(payRules) {
  const mapPayRules = {};
  let payTitle = '';
  payRules?.forEach(item => {
    payTitle = item.IsMultiplier ? 'Weekly OT 1.5x' : 'Hourly';
    mapPayRules[item.Id] = {
      field: 'HourlyRate',
      value: `${item.HourlyRate || ''}`,
      payTitle: payTitle,
    };
  });
  return mapPayRules;
}

export function getContractOverrideMapFixedRate(payRules) {
  const mapPayRules = {};
  payRules?.forEach(item => {
    mapPayRules[item.Id] = {
      field: 'HourlyRate',
      value: `${item.HourlyRate}`,
      payTitle: 'Fixed Rate',
    };
  });
  return mapPayRules;
}

/**
 * Split the ID into countryFolder and fileName to return an object with:
 *  - TemplateEmploymentTermCountryFolder
 *  - TemplateEmploymentTermFileName
 *
 * @param {String} id
 * @return {{}|{TemplateEmploymentTermCountryFolder: *, TemplateEmploymentTermFileName: *}}
 */
export function getEmploymentTermCountryFolderAndFileByFormat(id) {
  const [TemplateEmploymentTermCountryFolder, TemplateEmploymentTermFileName] =
    id?.split('/') || [];
  if (!TemplateEmploymentTermCountryFolder || !TemplateEmploymentTermFileName) {
    return {};
  }
  return {
    TemplateEmploymentTermCountryFolder,
    TemplateEmploymentTermFileName,
  };
}

/*
  @Param awardId
  - In Enterprise:
    + Can be a position number (an existing contract ID).
    + Can be a string (a PayPolicyID from PayRatesBuilder) following the pattern [a-zA-Z0-9]{27}.
      -> However, it must be TermID in ContractObject.
  - In Premium:
    + Supports all cases from Enterprise.
    + Additionally, it can be a string representing a TemplateTermID in the format {{countryFolder}}/{{fileName}}.
      -> However, it must be ID in ContractObject.
 */
export function getTemplateTermParams(awardId, payDetail, level, levelVisible) {
  const intAwardId = parseInt(awardId);
  const objParam = {};

  if (isEnterprise() && typeof awardId === 'string') {
    const awardInfo = payDetail?.contractObject?.TermId?.split('/');
    if (!awardInfo) {
      return objParam;
    }
    objParam.TemplateEmploymentTermCountryFolder = awardInfo[0];
    objParam.TemplateEmploymentTermFileName = awardInfo[1];
  } else if (
    getPayCenterConfiguration().isMultiPayDetailsAvailableOnPremium() &&
    typeof awardId === 'string'
  ) {
    const termId = payDetail?.contractObject?.IsPayPolicy
      ? payDetail?.contractObject?.TermId
      : payDetail?.contractObject?.Id; // RatePerDay, Hourly 44, Fixed Rate

    Object.assign(
      objParam,
      getEmploymentTermCountryFolderAndFileByFormat(termId || awardId)
    );
  } else if (intAwardId) {
    objParam.TemplateEmploymentTerm = intAwardId;
  } else if (typeof awardId === 'string' && isNotABP(awardId)) {
    Object.assign(
      objParam,
      getEmploymentTermCountryFolderAndFileByFormat(awardId)
    );
  }
  if (levelVisible) {
    objParam.TemplateEmploymentTermLevel = level || payDetail?.level;
  }

  return objParam;
}

function getTemplateTermParamsLibraryABP(
  awardId,
  parentAward,
  level,
  levelVisible
) {
  const objParam = {};

  objParam.TemplateEmploymentTerm = parentAward;
  if (typeof awardId === 'string' && isNotABP(awardId)) {
    const awardInfo = awardId.split('/');
    objParam.TemplateEmploymentTermCountryFolder = awardInfo[0];
    objParam.TemplateEmploymentTermFileName = awardInfo[1];
  }
  if (levelVisible) {
    objParam.TemplateEmploymentTermLevel = level;
  }

  return objParam;
}

export function getTemplateTermParamsCustomAwards(
  awardId,
  contractName,
  identifier
) {
  const intAwardId = parseInt(awardId);
  const objParam = {};

  if (intAwardId) {
    objParam.CustomEmploymentTerm = intAwardId;
    objParam.CustomEmploymentTermIdentifier = identifier;
    objParam.CustomEmploymentTermName = contractName;
  }

  return objParam;
}

export function getTemplateTermParamsCustomAwardsABP(awardId) {
  const intAwardId = parseInt(awardId);
  const objParam = {};

  if (intAwardId) {
    objParam.CustomEmploymentTerm = intAwardId;
  }

  return objParam;
}

export function getTemplateTermParamsRatesPerDayABP(awardId) {
  const intAwardId = parseInt(awardId);
  const objParam = {};

  if (intAwardId) {
    objParam.TemplateEmploymentTerm = intAwardId;
  } else if (isNotABP(awardId)) {
    const awardInfo = awardId.split('/');
    objParam.TemplateEmploymentTerm = awardId;
    objParam.TemplateEmploymentTermCountryFolder = awardInfo[0];
    objParam.TemplateEmploymentTermFileName = awardInfo[1];
  }

  return objParam;
}

export function getRequestObjectLibraryAward(
  awardId,
  basePayRule,
  payRules,
  skipABP = false,
  index = 0,
  renderKey,
  level,
  contractLevels = undefined
) {
  const employmentState = useEmploymentStore();
  const localPayDetail =
    typeof renderKey === 'number'
      ? employmentState.getPayDetailByRenderKey(renderKey)
      : employmentState.localPayDetail[index];
  const stateAreaBasedPay = skipABP ? null : localPayDetail?.areaBasedPayV2;
  const levelVisible = checkSelectLevelVisible(
    contractLevels ?? localPayDetail?.contractLevels
  );
  const templateTermParams = getTemplateTermParams(
    awardId,
    localPayDetail,
    level,
    levelVisible
  );

  if (
    !skipABP &&
    stateAreaBasedPay &&
    stateAreaBasedPay.areaSelected.length &&
    stateAreaBasedPay.areaGroups.length
  ) {
    // Area based pay is being used

    return {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
      AreaOfWorkDefaultRates: {
        ...templateTermParams,
        TemplateEmploymentTermOverrideMap: getContractOverrideMap(
          basePayRule,
          payRules
        ),
      },
      ...stateAreaBasedPay.dataMap,
    };
  } else {
    return {
      PayRatesType: AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Id,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap: getContractOverrideMap(
        basePayRule,
        payRules
      ),
    };
  }
}

export function getRequestObjectLibraryAwardABP(areaGroups, awardId) {
  const rootState = useRootStore();
  const arrRequest = [];

  areaGroups?.forEach(areaGroup => {
    const selectedTerm = rootState.setup.EmploymentTermArray.find(
      term => term.Id === areaGroup.contract
    );
    let newAwardId = awardId;
    if (selectedTerm?.IsPayPolicy) {
      newAwardId = areaGroup.contract;
    }

    const areaIds = areaGroup.areas?.map(area => area.link);
    const workAreas = areaIds;
    const levelVisible = checkSelectLevelVisible(areaGroup.contractLevels);
    const templateTermParams = getTemplateTermParamsLibraryABP(
      areaGroup.contract,
      newAwardId,
      areaGroup.level,
      levelVisible
    );
    const TemplateEmploymentTermOverrideMap = getContractOverrideMap(
      areaGroup.basePayRule,
      areaGroup.payRules
    );

    arrRequest.push({
      workAreas,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap,
    });
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

export function getRequestObjectCustomAward(
  awardId,
  contractName,
  identifier,
  basePayRule,
  payRules,
  skipABP = false,
  areaBasedPayV2 = null
) {
  const employmentState = useEmploymentStore();
  areaBasedPayV2 = skipABP
    ? null
    : employmentState?.localPayDetail[0]?.areaBasedPayV2;

  if (!basePayRule || Object.keys(basePayRule).length === 0) {
    return;
  }

  const templateTermParams = getTemplateTermParamsCustomAwards(
    awardId,
    contractName,
    identifier
  );

  if (
    !skipABP &&
    areaBasedPayV2?.areaSelected.length &&
    areaBasedPayV2?.areaGroups.length
  ) {
    // Area based pay is being used
    return {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
      AreaOfWorkDefaultRates: {
        ...templateTermParams,
        CustomEmploymentTermOverrideMap: getContractOverrideMap(
          basePayRule,
          payRules
        ),
      },
      ...areaBasedPayV2.dataMap,
    };
  } else {
    return {
      PayRatesType: AWARDS_TYPE_ENUM.CUSTOM_TERM.Id,
      ...templateTermParams,
      CustomEmploymentTermOverrideMap: getContractOverrideMap(
        basePayRule,
        payRules
      ),
    };
  }
}

export function getRequestObjectCustomAwardABP(areaGroups) {
  const arrRequest = [];
  areaGroups?.forEach(areaGroup => {
    const areaIds = areaGroup.areas?.map(area => area.link);
    const workAreas = areaIds;

    const templateTermParams = getTemplateTermParamsCustomAwardsABP(
      areaGroup.contract
    );
    const CustomEmploymentTermOverrideMap = getContractOverrideMap(
      areaGroup.basePayRule,
      areaGroup.payRules
    );

    arrRequest.push({
      workAreas,
      ...templateTermParams,
      CustomEmploymentTermOverrideMap,
    });
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

export function getRequestObjectRatesPerDay(
  awardId,
  payRules,
  skipABP = false,
  index = 0
) {
  const employmentState = useEmploymentStore();
  const localPayDetail = employmentState.localPayDetail[index];
  const stateAreaBasedPay = skipABP ? null : localPayDetail?.areaBasedPayV2;

  if (
    !skipABP &&
    stateAreaBasedPay &&
    stateAreaBasedPay.areaSelected.length &&
    stateAreaBasedPay.areaGroups.length
  ) {
    // Area based pay is being used
    const templateTermParams = getTemplateTermParams(awardId, localPayDetail);
    return {
      PayRatesType: AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id,
      AreaOfWorkDefaultRates: {
        ...templateTermParams,
        TemplateEmploymentTermOverrideMap: getContractOverrideMapRatesPerDay(
          payRules
        ),
      },
      ...stateAreaBasedPay.dataMap,
    };
  } else {
    const templateTermParams = getTemplateTermParams(awardId, localPayDetail);
    return {
      PayRatesType: AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Id,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap: getContractOverrideMapRatesPerDay(
        payRules
      ),
    };
  }
}

export function getRequestObjectRatesPerDayABP(areaGroups, awardId) {
  const arrRequest = [];
  areaGroups?.forEach(areaGroup => {
    const areaIds = areaGroup.areas?.map(area => area.link);
    const workAreas = areaIds;

    const templateTermParams = getTemplateTermParamsRatesPerDayABP(awardId);
    const TemplateEmploymentTermOverrideMap = getContractOverrideMapRatesPerDay(
      areaGroup.payRules
    );

    arrRequest.push({
      workAreas,
      ...templateTermParams,
      TemplateEmploymentTermOverrideMap,
    });
  });

  return {
    AreaOfWorkPayRules: arrRequest,
  };
}

export function humanizeLibraryPayRates(
  payRates,
  basePayRate,
  employeeHourlyRate,
  payRuleOverridesMap = null,
  levels = []
) {
  const CURRENCY_SYMBOL = getCurrencySymbol();

  const baseRate =
    employeeHourlyRate !== null ? employeeHourlyRate : basePayRate.HourlyRate;
  return payRates.map(item => {
    let amount = 0;
    let multiplierTarget = 'Base rate';
    const overrideValue = payRuleOverridesMap?.[item.Id];
    if (overrideValue || overrideValue === 0) {
      amount = overrideValue;
    } else if (item.Id === basePayRate.Id) {
      amount = baseRate;
    } else if (item.HourlyRate) {
      amount = item.HourlyRate;
    } else if (item.UnitValue) {
      amount = item.UnitValue;
    } else if (item.MultiplierValue) {
      amount = formatMultiplierRate(baseRate * item.MultiplierValue);
      const level = getPayRuleMultiplierLevel(item, levels);
      if (level) {
        multiplierTarget = level.Name ?? level.Title;
        amount = formatMultiplierRate(level.BaseRate * item.MultiplierValue);
      }
    }
    return {
      label: item.PayTitle,
      value: `${CURRENCY_SYMBOL}${amount} ${
        item.RemunerationType === PAY_RULE_ENUM.REMUNERATION_TYPE.UNIT.Id
          ? 'per unit'
          : 'per hour'
      }`,
      multiplier: item.MultiplierValue
        ? ` (${multiplierTarget} x ${item.MultiplierValue})`
        : '',
    };
  });
}

export function humanizeSimpleRates(payRules) {
  const CURRENCY_SYMBOL = getCurrencySymbol();

  return payRules?.map(item => {
    return {
      label: item.PayTitle,
      value: `${CURRENCY_SYMBOL}${item.HourlyRate} per hour`,
    };
  });
}

export function getBasicPayRateType(contactId) {
  let payRateType;
  switch (contactId) {
    case AWARDS_TYPE_ENUM.HOURLY.Id:
      payRateType = AWARDS_TYPE_ENUM.HOURLY.Code;
      break;

    case AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Id:
      payRateType = AWARDS_TYPE_ENUM.HOURLY_FLAT_40_AND_TIMEHALF_OVERTIME.Code;
      break;

    case AWARDS_TYPE_ENUM.SALARY.Id:
      payRateType = AWARDS_TYPE_ENUM.SALARY.Code;
      break;

    case AWARDS_TYPE_ENUM.RATES_PER_DAY.Id:
      payRateType = AWARDS_TYPE_ENUM.RATES_PER_DAY.Code;
      break;

    case AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Id:
      payRateType = AWARDS_TYPE_ENUM.LIBRARY_AWARD_TEMPLATE.Code;
      break;

    case AWARDS_TYPE_ENUM.CUSTOM_TERM.Id:
      payRateType = AWARDS_TYPE_ENUM.CUSTOM_TERM.Code;
      break;

    case AWARDS_TYPE_ENUM.EXTERNAL.Id:
      payRateType = AWARDS_TYPE_ENUM.EXTERNAL.Code;
      break;

    case AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Id:
      payRateType = AWARDS_TYPE_ENUM.ABP_TYPE_ENUM.Code;
      break;
  }

  return payRateType;
}

/*
 * Get the payroll service which is connected at present
 *
 * It returns the service key from THIRD_PARTY_SYNC which is connected.
 * (At present this may be only Xero. In theory there can be different services connected).
 */

export function getConnectedPayrollService(intWorkplaceIdArray) {
  const rootState = useRootStore();
  let strReturn = '';
  const intConnectedServiceMap = {};
  let blnSomeLocationIsConnected = false;
  const allLocations = rootState.setup.WorkplaceArray || [];

  intWorkplaceIdArray?.forEach(intWorkplaceId => {
    const workplace = allLocations?.find(x => x.Id == intWorkplaceId);

    if (workplace?.IsMyWorkplace) {
      const configObject = workplace.ConnectedPayrollMap;

      if (!configObject) {
        return;
      }

      Object.keys(configObject)?.forEach(strPayrollServicePrefix => {
        const blnConnected = configObject[strPayrollServicePrefix];

        if (blnConnected) {
          if (intConnectedServiceMap[strPayrollServicePrefix] === undefined) {
            intConnectedServiceMap[strPayrollServicePrefix] = 0;
          }

          intConnectedServiceMap[strPayrollServicePrefix]++;
          blnSomeLocationIsConnected = true;
        }
      });
    }
  });

  if (blnSomeLocationIsConnected) {
    let intMaxCount = 0;

    Object.keys(intConnectedServiceMap)?.forEach(strPayrollServicePrefix => {
      const intCount = intConnectedServiceMap[strPayrollServicePrefix];

      if (intCount > intMaxCount) {
        intMaxCount = intCount;
        strReturn = strPayrollServicePrefix;
      }
    });
  }

  return strReturn;
}

export function getSyncTime(thirdPartySync = {}) {
  const dateObject = DateTime.fromISO(thirdPartySync.SyncTimestampLocalized);

  if (!dateObject.isValid) {
    return '';
  }

  // TODO: replace this with webappStore.getBusinessInfo
  let dateFormat = userSession()?.DefaultShortDateFormat;
  let timeFormat = userSession()?.DefaultShortTimeFormat;
  dateFormat = dateFormat.replace('D', 'ccc').replace('m', 'M');
  timeFormat = timeFormat.replace('i', 'm').replace('A', 'a');

  return dateObject.toFormat(`${dateFormat} ${timeFormat}`);
}

/**
 * @deprecated // currencyFormatBasedOnUserLocale(value, {decimal: 2}) frontend/vnext/src/lib/currency-format-based-on-user-locale.js
 * @param {String|Number} fltValue
 * @return {Number}
 */
export function formatMultiplierRate(fltValue) {
  if (!fltValue) {
    return fltValue;
  }
  return parseFloat(fltValue.toFixed(2).toString());
}

export function isNonZeroPayRate(payRate = '0') {
  return parseFloat(payRate) > 0;
}

export function findNonZeroPayRate(payRules = []) {
  return !!payRules.find(payRule => isNonZeroPayRate(payRule?.HourlyRate));
}

export function getDefaultLeaveRules(leaveRules = []) {
  // LBU-78 default leave rule for all pay library that has no leave rule attached
  // would be annual leave, sick leave and unpaid leave.

  const leavePayrollCategory = [
    LEAVES.SICK_LEAVE,
    LEAVES.ANNUAL_LEAVE,
    LEAVES.UNPAID_LEAVE,
  ];

  const intDefaultLeaveRuleMap = {};
  leaveRules.forEach(leaveRule => {
    if (leavePayrollCategory.includes(leaveRule.PayrollCategory)) {
      intDefaultLeaveRuleMap[leaveRule.PayrollCategory] = leaveRule;
    }
  });

  return Object.values(intDefaultLeaveRuleMap);
}

export function getSalaryRequestPayload(salaryState) {
  const days = [];

  salaryState?.weekDaysSalaryAllocation?.forEach(strDay => {
    days.push(SALARY_DISTRIBUTION_ENUM.DAY_OF_WEEK.DAY_TO_INT[strDay]);
  });

  const daysPerWeek = 5;
  const hoursPerDay = 8;
  // let standardHour = 8;
  let hourlyRateOfSalary = 0;

  if (salaryState.salaryAmount) {
    hourlyRateOfSalary = (
      salaryState.salaryAmount /
      ((365 / 7) * daysPerWeek * hoursPerDay)
    )?.toFixed(4);
  }

  const payLoad = {
    PayRatesType: AWARDS_TYPE_ENUM.SALARY.Id,
    SalaryAssocAreasForScheduled:
      salaryState?.isAllocateToOptionVisible &&
      salaryState?.isAreaSelectionVisible
        ? 1
        : 0,
    SalaryDaysOfWeekArray: days,
    SalaryDistributionType: salaryState.salaryCostAllocation,
  };

  payLoad[salaryState.salaryPeriod] = salaryState.salaryAmount;

  if (salaryState.isAreaSelectionVisible) {
    payLoad['SalaryAreasArray'] = [
      ...salaryState.areasSelected.map(x => x.link),
    ];
  }

  if (
    salaryState.isAllocateToOptionVisible &&
    !salaryState.isAreaSelectionVisible
  ) {
    payLoad['SalaryStandardHours'] = 8; // TODO: use actual standard hours
    payLoad['SalaryHourlyRate'] = hourlyRateOfSalary;
  }

  return payLoad;
}

export function getExternalRequestPayload(payDetail) {
  const payload = {
    PayRatesType: AWARDS_TYPE_ENUM.EXTERNAL.Id,
  };

  if (!payDetail?.thirdPartySync) {
    payload.PullExternalRates = 1;
  }
  return payload;
}

export function filterAreaRatesByAreas(payRates, agreement) {
  const employmentState = useEmploymentStore();
  const rootState = useRootStore();
  const payDetail = employmentState.getPayDetailByAgreementId(agreement.Id);

  const filteredPayData = { ...payRates };
  const payDetailLocationIds =
    payDetail?.locations?.map(location => location.Id) || [];
  let filteredAreas = rootState.getAreaIdsByLocationId(payDetailLocationIds);

  if (
    !filteredPayData ||
    !filteredPayData.success ||
    !Array.isArray(filteredAreas)
  ) {
    filteredAreas = [];
  }

  const filteredAreaRates = filteredPayData.areaRates.filter(areaRate => {
    return areaRate.areaIds.some(areaId => filteredAreas.includes(areaId));
  });

  filteredPayData.areaRates = [...filteredAreaRates];

  return filteredPayData;
}

/**
 * Returns an array of employment types according to country code
 *
 * @param {String} countryCode
 *
 * @return {Array}
 */
export function getEmploymentTypesByCountry(countryCode) {
  let employmentTypes = null;
  if (countryCode == COUNTRY_CODE.AU) {
    employmentTypes = [
      { value: 1, label: 'Full time' },
      { value: 2, label: 'Part time' },
      { value: 3, label: 'Casual' },
      { value: 4, label: 'Contractor' },
    ];
  } else if (countryCode == COUNTRY_CODE.US || countryCode == COUNTRY_CODE.GB) {
    employmentTypes = [
      { value: 1, label: 'Full time' },
      { value: 2, label: 'Part time' },
      { value: 3, label: 'Casual' },
      { value: 4, label: 'Contractor' },
      { value: 5, label: 'Hourly' },
      { value: 6, label: 'Salary' },
    ];
  } else {
    employmentTypes = [
      { value: 1, label: 'Full time' },
      { value: 2, label: 'Part time' },
      { value: 3, label: 'Casual' },
      { value: 4, label: 'Contractor' },
    ];
  }
  return employmentTypes;
}

export function sortPayRulesForDisplay(payRules) {
  return ['weekday', 'saturday', 'sunday', 'public holiday'].map(key => {
    return payRules?.find(payRule => {
      return payRule?.PayTitle?.toLowerCase()?.includes(key);
    });
  });
}

export function getPayRuleMultiplierLevel(payRule, levels) {
  if (payRule.IsMultiplier) {
    const levelKsuid = payRule.Config?.payConfig?.classificationReference;
    if (levelKsuid) {
      return levels.find(level => level.LevelKsuid === levelKsuid);
    }
  }
  return null;
}
